// Minimal typed fetch wrapper. Reads JWT + tenant slug from the auth store so
// every request carries them automatically. 401 triggers a silent refresh via
// a shared refresh queue — concurrent in-flight requests all wait on a single
// /auth/refresh call, then retry with the new token.

import { env } from '@/env'
import { useAuthStore } from '@/stores/auth'

export interface ApiResponse<T> {
  data: T | null
  error: { code: string; message: string; details?: unknown } | null
  meta: Record<string, unknown>
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: unknown
  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message)
    this.code = code
    this.status = status
    this.details = details
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Marks polling/background traffic that must not invalidate auth on a lone 401. */
  background?: boolean
  /** Skip auth header — used by public endpoints like login. */
  skipAuth?: boolean
  /** Skip tenant slug header — used by `/api/v1/tenants/register`. */
  skipTenant?: boolean
  /** Internal flag — prevents infinite loops if /auth/refresh itself returns 401. */
  _retry?: boolean
  /** Internal flag — return the full { data, error, meta } envelope, not just data. */
  _envelope?: boolean
}

const AUTH_401_WINDOW_MS = 30_000
const AUTH_401_THRESHOLD = 3
const recent401At: number[] = []

// ── Refresh queue ────────────────────────────────────────────────────────

let isRefreshing = false
interface PendingRequest { resolve: (token: string) => void; reject: (err: Error) => void }
const pendingQueue: PendingRequest[] = []

async function refreshToken(): Promise<string> {
  const { refreshToken: rt, tenantSlug } = useAuthStore.getState()
  if (!rt) throw new ApiError('NO_REFRESH_TOKEN', 'No refresh token', 401)
  // Carry the tenant slug like every other request so the API never has to fall
  // back to host-subdomain slug derivation (which 404s on infra hostnames).
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (tenantSlug) headers['x-tenant-slug'] = tenantSlug
  const res = await fetch(`${env.VITE_API_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ refreshToken: rt }),
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError('REFRESH_FAILED', 'Refresh failed', res.status)
  const body = (await res.json()) as ApiResponse<{ accessToken: string; refreshToken: string }>
  if (!body.data) throw new ApiError('REFRESH_FAILED', 'Refresh failed', res.status)
  useAuthStore.getState().setTokens(body.data.accessToken, body.data.refreshToken)
  return body.data.accessToken
}

function onAuthFailure() {
  useAuthStore.getState().clear()
  // Hard redirect — the auth store is the source of truth for every query.
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login?reason=expired')
  }
}

async function waitForRefresh(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    pendingQueue.push({ resolve, reject })
  })
}

async function triggerRefresh(): Promise<string> {
  if (isRefreshing) return waitForRefresh()
  isRefreshing = true
  try {
    const token = await refreshToken()
    for (const p of pendingQueue.splice(0)) p.resolve(token)
    return token
  } catch (err) {
    const e = err instanceof Error ? err : new Error(String(err))
    for (const p of pendingQueue.splice(0)) p.reject(e)
    throw e
  } finally {
    isRefreshing = false
  }
}

function pruneRecent401s(now: number): number[] {
  while (recent401At.length > 0 && now - recent401At[0]! > AUTH_401_WINDOW_MS) {
    recent401At.shift()
  }
  return recent401At
}

function record401(now = Date.now()): number {
  recent401At.push(now)
  return pruneRecent401s(now).length
}

function log401Event(params: {
  endpoint: string
  background: boolean
  refreshAttempted: boolean
  logoutTriggered: boolean
  recent401Count: number
}) {
  console.warn('[api] 401 response', params)
}

function get401Strategy(url: URL, opts: ApiOptions): 'ignore' | 'refresh' | 'logout' {
  const recent401Count = record401()
  const background = opts.background === true
  if (recent401Count >= AUTH_401_THRESHOLD) {
    log401Event({
      endpoint: `${url.pathname}${url.search}`,
      background,
      refreshAttempted: opts._retry === true,
      logoutTriggered: true,
      recent401Count,
    })
    return 'logout'
  }

  if (background) {
    log401Event({
      endpoint: `${url.pathname}${url.search}`,
      background: true,
      refreshAttempted: false,
      logoutTriggered: false,
      recent401Count,
    })
    return 'ignore'
  }

  if (opts._retry) {
    log401Event({
      endpoint: `${url.pathname}${url.search}`,
      background: false,
      refreshAttempted: true,
      logoutTriggered: true,
      recent401Count,
    })
    return 'logout'
  }

  log401Event({
    endpoint: `${url.pathname}${url.search}`,
    background: false,
    refreshAttempted: true,
    logoutTriggered: false,
    recent401Count,
  })
  return 'refresh'
}

export function resetApiClientStateForTests() {
  isRefreshing = false
  pendingQueue.splice(0)
  recent401At.splice(0)
}

// ── Request core ─────────────────────────────────────────────────────────

/** True if the access JWT expires within 5 minutes (parse-fail/expired → false,
 *  the 401 path is the fallback). Lets us refresh proactively before a request. */
function accessTokenExpiringSoon(token: string): boolean {
  try {
    const payload = token.split('.')[1]
    if (!payload) return false
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number }
    if (typeof claims.exp !== 'number') return false
    return claims.exp * 1000 - Date.now() < 5 * 60 * 1000
  } catch {
    return false
  }
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { tenantSlug } = useAuthStore.getState()
  let accessToken = useAuthStore.getState().accessToken
  // Proactive refresh: if the access token is about to expire, silently refresh
  // it BEFORE sending so an active user never hits a 401 mid-action. Reuses the
  // single-flight triggerRefresh() so concurrent calls share one network round-trip.
  if (!opts.skipAuth && !opts._retry && accessToken && accessTokenExpiringSoon(accessToken)) {
    accessToken = await triggerRefresh().catch(() => accessToken)
  }
  const url = new URL(path.startsWith('http') ? path : `${env.VITE_API_URL}${path}`)
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v != null) url.searchParams.set(k, String(v))
    }
  }

  // Only attach a content-type header when there's a body to describe.
  // Fastify's JSON parser rejects any request that advertises
  // `content-type: application/json` but ships no body with
  // FST_ERR_CTP_EMPTY_JSON_BODY — which broke every DELETE and every
  // bodyless POST coming out of this client.
  const hasBody = opts.body !== undefined
  const headers: Record<string, string> = {}
  if (hasBody) headers['content-type'] = 'application/json'
  if (!opts.skipAuth && accessToken) headers.authorization = `Bearer ${accessToken}`
  if (!opts.skipTenant && tenantSlug) headers['x-tenant-slug'] = tenantSlug

  const res = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    headers,
    body: hasBody ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'include',
  })

  if (res.status === 204) return undefined as T

  if (res.status === 401 && !opts.skipAuth) {
    const strategy = get401Strategy(url, opts)
    if (strategy === 'logout') {
      onAuthFailure()
      throw new ApiError('UNAUTHENTICATED', 'errors.auth.unauthenticated', 401)
    }
    if (strategy === 'refresh') {
      try {
        await triggerRefresh()
      } catch {
        log401Event({
          endpoint: `${url.pathname}${url.search}`,
          background: false,
          refreshAttempted: true,
          logoutTriggered: true,
          recent401Count: pruneRecent401s(Date.now()).length,
        })
        onAuthFailure()
        throw new ApiError('UNAUTHENTICATED', 'errors.auth.unauthenticated', 401)
      }
      return api<T>(path, { ...opts, _retry: true })
    }
  }

  let json: ApiResponse<T>
  try {
    json = (await res.json()) as ApiResponse<T>
  } catch {
    throw new ApiError('PARSE_ERROR', `Invalid JSON response (${res.status})`, res.status)
  }

  if (!res.ok || json.error) {
    const err = json.error ?? { code: 'HTTP_ERROR', message: `HTTP ${res.status}` }
    throw new ApiError(err.code, err.message, res.status, err.details)
  }
  return (opts._envelope ? json : json.data) as T
}

// Convenience wrappers (keep route definitions terse at call sites).
export const get    = <T>(p: string, o?: ApiOptions) => api<T>(p, { ...o, method: 'GET' })
/** GET returning the full envelope ({ data, error, meta }) — for paginated lists that need meta.total. */
export const getWithMeta = <T>(p: string, o?: ApiOptions) => api<ApiResponse<T>>(p, { ...o, method: 'GET', _envelope: true })
export const post   = <T>(p: string, body?: unknown, o?: ApiOptions) => api<T>(p, { ...o, method: 'POST', body })
export const patch  = <T>(p: string, body?: unknown, o?: ApiOptions) => api<T>(p, { ...o, method: 'PATCH', body })
export const del    = <T>(p: string, body?: unknown, o?: ApiOptions) => api<T>(p, { ...o, method: 'DELETE', body })
